import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeft, BookOpen, CalendarCheck, ChevronRight, Download, GraduationCap, Search, Wallet } from 'lucide-react';

import PageContainer from '../components/PageContainer';
import { PayrollRangePicker } from '../components/payroll/PayrollRangePicker';
import { Button, EmptyState, ErrorState, Input, LoadingState, SearchSelect, SectionPanel, Select, StatusBadge, useToast } from '../components/ui';
import { reportsApi } from '../services/api';
import type {
    ReportCatalog,
    ReportCategory,
    ReportColumnType,
    ReportFilterValues,
    ReportPreview,
    ReportStaffRole,
    ReportStatusFilter,
    ReportSummary,
    ReportValue,
} from '../types';
import { cn } from '../utils/cn';
import { formatDateKey, formatVnd } from '../utils/money';
import { classOptionsByCampus } from '../utils/pickerOptions';
import { matchesQuery } from '../utils/search';

const CATEGORIES: Array<{ id: ReportCategory; label: string; icon: typeof BookOpen }> = [
    { id: 'students', label: 'Học sinh & lớp', icon: GraduationCap },
    { id: 'attendance', label: 'Điểm danh & chuyên cần', icon: CalendarCheck },
    { id: 'learning', label: 'Học tập', icon: BookOpen },
    { id: 'staff', label: 'Nhân sự & chấm công', icon: Wallet },
];

const KIND_LABELS = { data: 'Dữ liệu', report: 'Báo cáo' } as const;
const KIND_HINTS = {
    data: 'Mỗi dòng một bản ghi, để lọc và tính tiếp trong Excel.',
    report: 'Số liệu đã tổng hợp sẵn.',
} as const;

const STATUS_LABELS: Record<ReportStatusFilter, string> = {
    active: 'Đang hoạt động',
    inactive: 'Đã nghỉ / ngưng',
    all: 'Mọi trạng thái',
};

const ROLE_LABELS: Record<ReportStaffRole, string> = {
    TEACHER: 'Giáo viên',
    ASSISTANT: 'Trợ giảng',
    MANAGER: 'Quản lý',
    ADMIN: 'Quản trị viên',
};
const ROLES = Object.keys(ROLE_LABELS) as ReportStaffRole[];

const FILTER_PARAMS = ['from', 'to', 'campusId', 'classId', 'studentId', 'studentStatus', 'roles', 'staffId'] as const;

function formatValue(value: ReportValue, type: ReportColumnType = 'text') {
    if (value === null || value === undefined || value === '') return '';
    if (typeof value === 'number') {
        if (type === 'money') return formatVnd(value);
        if (type === 'percent') return `${(value * 100).toLocaleString('vi-VN', { maximumFractionDigits: 1 })}%`;
        return value.toLocaleString('vi-VN');
    }
    if (type === 'date' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return formatDateKey(value);
    return value;
}

function saveFile(blob: Blob, fileName: string) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function ReportCard({ report, onOpen }: { report: ReportSummary; onOpen: () => void }) {
    return (
        <button
            type="button"
            onClick={onOpen}
            className="flex w-full items-start justify-between gap-3 rounded-lg border border-ink-200 bg-white p-4 text-left transition-colors hover:border-academy-300 hover:bg-academy-50/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-academy-200"
        >
            <span className="min-w-0">
                <span className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-ink-900">{report.title}</span>
                    <StatusBadge tone={report.kind === 'report' ? 'info' : 'neutral'}>{KIND_LABELS[report.kind]}</StatusBadge>
                </span>
                <span className="mt-1 block text-sm text-ink-600">{report.description}</span>
            </span>
            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-ink-400" />
        </button>
    );
}

// The list of reports by category, with a search.
function ReportList({ reports, onOpen }: { reports: ReportSummary[]; onOpen: (id: string) => void }) {
    const [query, setQuery] = useState('');
    const matching = reports.filter(report => matchesQuery(`${report.title} ${report.description}`, query));
    return (
        <div className="space-y-5">
            <Input
                aria-label="Tìm báo cáo"
                placeholder="Tìm báo cáo: điểm danh, chấm công, học sinh…"
                leftIcon={<Search className="h-4 w-4" />}
                value={query}
                onChange={event => setQuery(event.target.value)}
                className="sm:w-96"
            />
            {!matching.length ? <EmptyState title="Không có báo cáo nào khớp" description="Thử tìm bằng từ khác." /> : null}
            {CATEGORIES.map(category => {
                const items = matching.filter(report => report.category === category.id);
                if (!items.length) return null;
                const Icon = category.icon;
                return (
                    <section key={category.id} aria-label={category.label} className="space-y-2">
                        <h2 className="flex items-center gap-2 text-sm font-semibold text-ink-700">
                            <Icon className="h-4 w-4 text-ink-500" />
                            {category.label}
                        </h2>
                        <div className="grid gap-2 md:grid-cols-2">
                            {items.map(report => <ReportCard key={report.id} report={report} onOpen={() => onOpen(report.id)} />)}
                        </div>
                    </section>
                );
            })}
        </div>
    );
}

// Several roles at once; none chosen means every role.
function RoleFilter({ value, onChange }: { value: ReportStaffRole[]; onChange: (roles: ReportStaffRole[]) => void }) {
    const toggle = (role: ReportStaffRole) => onChange(value.includes(role) ? value.filter(item => item !== role) : ROLES.filter(item => item === role || value.includes(item)));
    const chip = 'min-h-9 rounded-full border px-3 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-academy-200';
    return (
        <div role="group" aria-label="Lọc theo vai trò" className="flex flex-wrap items-center gap-1.5">
            <button
                type="button"
                aria-pressed={!value.length}
                onClick={() => onChange([])}
                className={cn(chip, !value.length ? 'border-academy-500 bg-academy-50 font-medium text-academy-700' : 'border-ink-200 bg-white text-ink-600 hover:border-ink-300')}
            >
                Mọi vai trò
            </button>
            {ROLES.map(role => (
                <button
                    key={role}
                    type="button"
                    aria-pressed={value.includes(role)}
                    onClick={() => toggle(role)}
                    className={cn(chip, value.includes(role) ? 'border-academy-500 bg-academy-50 font-medium text-academy-700' : 'border-ink-200 bg-white text-ink-600 hover:border-ink-300')}
                >
                    {ROLE_LABELS[role]}
                </button>
            ))}
        </div>
    );
}

function PreviewTable({ preview }: { preview: Pick<ReportPreview, 'columns' | 'rows' | 'totals'> }) {
    const header = 'whitespace-nowrap px-3 py-2 text-left text-xs font-medium text-ink-500';
    const alignRight = (type?: ReportColumnType) => type === 'int' || type === 'money' || type === 'percent';
    return (
        <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-ink-200 text-sm">
                <thead className="bg-ink-50">
                    <tr>
                        {preview.columns.map(column => (
                            <th key={column.key} className={cn(header, alignRight(column.type) && 'text-right')}>{column.label}</th>
                        ))}
                    </tr>
                </thead>
                <tbody className="divide-y divide-ink-100 bg-white">
                    {preview.rows.map((row, index) => (
                        <tr key={index}>
                            {preview.columns.map(column => (
                                <td key={column.key} className={cn('max-w-72 truncate px-3 py-2 text-ink-800', alignRight(column.type) && 'text-right tabular-nums')}>
                                    {formatValue(row[column.key], column.type)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
                {preview.totals ? (
                    <tfoot className="border-t border-ink-200 bg-ink-50 font-semibold">
                        <tr>
                            {preview.columns.map(column => (
                                <td key={column.key} className={cn('whitespace-nowrap px-3 py-2', alignRight(column.type) && 'text-right tabular-nums')}>
                                    {formatValue(preview.totals![column.key] ?? null, column.type)}
                                </td>
                            ))}
                        </tr>
                    </tfoot>
                ) : null}
            </table>
        </div>
    );
}

// One report: its filters, a preview of the first rows, and the Excel download with the same filters.
function ReportDetail({ report, catalog, filters, onFilters, onBack }: {
    report: ReportSummary;
    catalog: ReportCatalog;
    filters: ReportFilterValues;
    onFilters: (patch: Partial<ReportFilterValues>) => void;
    onBack: () => void;
}) {
    const { showToast } = useToast();
    const [preview, setPreview] = useState<ReportPreview | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [downloading, setDownloading] = useState(false);
    const requestRef = useRef(0);
    const has = (key: ReportSummary['filters'][number]) => report.filters.includes(key);
    const range = { from: filters.from ?? catalog.defaults.from, to: filters.to ?? catalog.defaults.to };
    // What gets sent: only this report's filters, with this month by default.
    const applied: ReportFilterValues = useMemo(() => ({
        ...(has('range') ? range : {}),
        ...(has('campus') && filters.campusId ? { campusId: filters.campusId } : {}),
        ...(has('class') && filters.classId ? { classId: filters.classId } : {}),
        ...(has('student') && filters.studentId ? { studentId: filters.studentId } : {}),
        ...(has('studentStatus') ? { studentStatus: filters.studentStatus ?? 'active' } : {}),
        ...(has('roles') && filters.roles ? { roles: filters.roles } : {}),
        ...(has('staff') && filters.staffId ? { staffId: filters.staffId } : {}),
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [report.id, range.from, range.to, filters.campusId, filters.classId, filters.studentId, filters.studentStatus, filters.roles, filters.staffId]);
    const waitingForClass = Boolean(report.requiresClass && !applied.classId);
    // Admins pick whose timesheet; anyone else has no picker and gets their own.
    const waitingForStaff = Boolean(report.requiresStaff && has('staff') && !applied.staffId);
    const waiting = waitingForClass || waitingForStaff;

    const load = useCallback(async () => {
        const requestId = ++requestRef.current;
        if (waiting) {
            setPreview(null);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError('');
        try {
            const response = await reportsApi.preview(report.id, applied);
            if (requestId === requestRef.current) setPreview(response.data.data);
        } catch (loadError: any) {
            if (requestId === requestRef.current) setError(loadError?.message || 'Không tải được báo cáo.');
        } finally {
            if (requestId === requestRef.current) setLoading(false);
        }
    }, [report.id, applied, waiting]);

    useEffect(() => {
        void load();
    }, [load]);

    const download = async () => {
        setDownloading(true);
        try {
            const { blob, fileName } = await reportsApi.download(report.id, applied);
            saveFile(blob, fileName ?? `${report.id}.xlsx`);
        } catch (downloadError: any) {
            showToast(downloadError?.message || 'Tải file thất bại.', 'error');
        } finally {
            setDownloading(false);
        }
    };

    const classOptions = classOptionsByCampus(catalog.options.classes, catalog.options.campuses, applied.campusId);
    // Students of the chosen class, or of the chosen campus's classes.
    const campusClassIds = new Set(catalog.options.classes.filter(item => !applied.campusId || item.campusId === applied.campusId).map(item => item.id));
    const studentOptions = catalog.options.students
        .filter(student => applied.classId ? student.classIds.includes(applied.classId) : student.classIds.some(id => campusClassIds.has(id)))
        .map(student => ({ value: student.id, label: student.name }));
    const roles = (applied.roles?.split(',') ?? []).filter((role): role is ReportStaffRole => ROLES.includes(role as ReportStaffRole));
    const staffOptions = catalog.options.staff
        .filter(person => !roles.length || roles.includes(person.role as ReportStaffRole))
        .map(person => ({
            value: person.id,
            label: `${person.name} · ${ROLE_LABELS[person.role as ReportStaffRole] ?? person.role}${person.isActive ? '' : ' (ngưng)'}`,
        }));

    return (
        <div className="space-y-4">
            <Button variant="ghost" size="sm" icon={<ArrowLeft className="h-4 w-4" />} onClick={onBack}>Tất cả báo cáo</Button>
            <SectionPanel className="space-y-4 p-4 sm:p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                        <h2 className="flex flex-wrap items-center gap-2 text-lg font-semibold text-ink-900">
                            {report.title}
                            <StatusBadge tone={report.kind === 'report' ? 'info' : 'neutral'}>{KIND_LABELS[report.kind]}</StatusBadge>
                        </h2>
                        <p className="mt-1 text-sm text-ink-600">{report.description}</p>
                        <p className="mt-0.5 text-xs text-ink-500">{KIND_HINTS[report.kind]}</p>
                    </div>
                    <Button
                        icon={<Download className="h-4 w-4" />}
                        loading={downloading}
                        disabled={waiting || Boolean(error)}
                        onClick={download}
                    >
                        Tải Excel
                    </Button>
                </div>
                <div role="group" aria-label="Bộ lọc báo cáo" className="flex flex-wrap items-center gap-2">
                    {has('range') ? <PayrollRangePicker value={range} onChange={next => onFilters(next)} /> : null}
                    {has('staff') ? (
                        <SearchSelect
                            aria-label="Chọn nhân sự"
                            className="min-h-9 lg:w-64"
                            value={applied.staffId ?? ''}
                            onChange={value => onFilters({ staffId: value || undefined })}
                            emptyOption="Chọn nhân sự"
                            options={staffOptions}
                        />
                    ) : null}
                    {has('campus') ? (
                        <Select
                            aria-label="Lọc theo cơ sở"
                            className="min-h-9 lg:w-48"
                            value={applied.campusId ?? ''}
                            onChange={event => onFilters({ campusId: event.target.value || undefined, classId: undefined, studentId: undefined })}
                        >
                            <option value="">Tất cả cơ sở</option>
                            {catalog.options.campuses.map(campus => <option key={campus.id} value={campus.id}>{campus.name}</option>)}
                        </Select>
                    ) : null}
                    {has('class') ? (
                        <SearchSelect
                            aria-label="Lọc theo lớp học"
                            className="min-h-9 lg:w-56"
                            value={applied.classId ?? ''}
                            onChange={value => onFilters({ classId: value || undefined, studentId: undefined })}
                            emptyOption={report.requiresClass ? 'Chọn lớp' : 'Tất cả lớp'}
                            options={classOptions}
                        />
                    ) : null}
                    {has('student') ? (
                        <SearchSelect
                            aria-label="Lọc theo học sinh"
                            className="min-h-9 lg:w-56"
                            value={applied.studentId ?? ''}
                            onChange={value => onFilters({ studentId: value || undefined })}
                            emptyOption="Tất cả học sinh"
                            options={studentOptions}
                        />
                    ) : null}
                    {has('studentStatus') ? (
                        <Select
                            aria-label="Lọc theo trạng thái"
                            className="min-h-9 lg:w-44"
                            value={applied.studentStatus ?? 'active'}
                            onChange={event => onFilters({ studentStatus: event.target.value as ReportStatusFilter })}
                        >
                            {(Object.keys(STATUS_LABELS) as ReportStatusFilter[]).map(status => (
                                <option key={status} value={status}>{STATUS_LABELS[status]}</option>
                            ))}
                        </Select>
                    ) : null}
                    {has('roles') ? <RoleFilter value={roles} onChange={next => onFilters({ roles: next.join(',') || undefined })} /> : null}
                </div>
            </SectionPanel>

            <SectionPanel aria-label="Xem trước" className="overflow-hidden p-0">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-ink-200 px-4 py-3">
                    <h3 className="text-sm font-semibold text-ink-900">Xem trước</h3>
                    {preview && !loading ? (
                        <p className="text-xs text-ink-500">
                            {preview.total > preview.rows.length
                                ? `${preview.rows.length} / ${preview.total.toLocaleString('vi-VN')} dòng đầu · file Excel có đủ ${preview.total.toLocaleString('vi-VN')} dòng`
                                : `${preview.total.toLocaleString('vi-VN')} dòng`}
                        </p>
                    ) : null}
                </div>
                {waitingForClass ? (
                    <EmptyState title="Chọn một lớp" description="Báo cáo này xem theo từng lớp." className="min-h-40" />
                ) : waitingForStaff ? (
                    <EmptyState title="Chọn một nhân sự" description="Báo cáo này xem theo từng người." className="min-h-40" />
                ) : error ? (
                    <ErrorState title="Không tải được báo cáo" description={error} onRetry={() => void load()} className="min-h-40" />
                ) : loading && !preview ? (
                    <LoadingState title="Đang tải báo cáo..." />
                ) : preview && !preview.total ? (
                    <EmptyState title="Không có dữ liệu" description="Thử đổi khoảng ngày hoặc bộ lọc." className="min-h-40" />
                ) : preview ? (
                    <div className={cn(loading && 'opacity-60')}>
                        {preview.info.length ? (
                            <dl className="flex flex-wrap gap-x-6 gap-y-1 border-b border-ink-100 px-4 py-3 text-sm">
                                {preview.info.map(([label, value]) => (
                                    <div key={label} className="flex gap-1.5">
                                        <dt className="text-ink-500">{label}:</dt>
                                        <dd className="font-medium text-ink-900">{value}</dd>
                                    </div>
                                ))}
                            </dl>
                        ) : null}
                        <PreviewTable preview={preview} />
                    </div>
                ) : null}
            </SectionPanel>
            {preview && !waiting && !error ? preview.extra.map(extra => (
                <SectionPanel key={extra.title} aria-label={extra.title} className="overflow-hidden p-0">
                    <div className="border-b border-ink-200 px-4 py-3">
                        <h3 className="text-sm font-semibold text-ink-900">{extra.title}</h3>
                        <p className="text-xs text-ink-500">Ở sheet riêng trong file Excel.</p>
                    </div>
                    <PreviewTable preview={extra.table} />
                </SectionPanel>
            )) : null}
        </div>
    );
}

export default function Reports() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [catalog, setCatalog] = useState<ReportCatalog | null>(null);
    const [error, setError] = useState('');
    const reportId = searchParams.get('report');

    const loadCatalog = useCallback(async () => {
        setError('');
        try {
            const response = await reportsApi.getCatalog();
            setCatalog(response.data.data);
        } catch (loadError: any) {
            setError(loadError?.message || 'Không tải được danh sách báo cáo.');
        }
    }, []);

    useEffect(() => {
        void loadCatalog();
    }, [loadCatalog]);

    // Filters live in the address, so a page's "Xuất" button can open a report already filtered.
    const filters: ReportFilterValues = Object.fromEntries(
        FILTER_PARAMS.map(key => [key, searchParams.get(key) || undefined]).filter(([, value]) => value),
    );
    const setFilters = (patch: Partial<ReportFilterValues>) => {
        const params = new URLSearchParams(searchParams);
        for (const [key, value] of Object.entries(patch)) {
            if (value) params.set(key, String(value));
            else params.delete(key);
        }
        setSearchParams(params, { replace: true });
    };
    const openReport = (id: string | null) => {
        const params = new URLSearchParams(searchParams);
        if (id) params.set('report', id);
        else {
            params.delete('report');
            FILTER_PARAMS.forEach(key => params.delete(key));
        }
        setSearchParams(params);
    };

    const report = catalog?.reports.find(item => item.id === reportId) ?? null;

    return (
        <PageContainer title="Dữ liệu & báo cáo" subtitle="Xem trước và tải Excel. Số liệu theo quyền của bạn.">
            {error ? (
                <ErrorState title="Không tải được danh sách báo cáo" description={error} onRetry={() => void loadCatalog()} />
            ) : !catalog ? (
                <LoadingState title="Đang tải danh sách báo cáo..." />
            ) : report ? (
                <ReportDetail key={report.id} report={report} catalog={catalog} filters={filters} onFilters={setFilters} onBack={() => openReport(null)} />
            ) : reportId ? (
                <div className="space-y-3">
                    <Button variant="ghost" size="sm" icon={<ArrowLeft className="h-4 w-4" />} onClick={() => openReport(null)}>Tất cả báo cáo</Button>
                    <EmptyState title="Không có báo cáo này" description="Báo cáo không tồn tại hoặc bạn không có quyền xem." />
                </div>
            ) : (
                <ReportList reports={catalog.reports} onOpen={id => openReport(id)} />
            )}
        </PageContainer>
    );
}
